import classNames from "classnames";
import { Link, useMatch } from "react-router-dom";

import { RobotoDivider } from "@/shared/components/RobotoDivider";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { SystemUserOnly } from "@/shared/components/SystemUserOnly";
import { useFeatureFlagStates } from "@/shared/domain/featureFlags/hooks";

import styles from "./Navigation.module.css";
import { navGroups, NavLink } from "./navigationLinks";

interface NavigationProps {
  /** Callback to be invoked when a navigation link is clicked. */
  onNavigate?: () => void;
}

/**
 * Sidebar navigation: feature links, settings/guides, and an admin block
 * for system users. Links declaring a `featureFlag` are hidden when that
 * flag is off for the current org, mirroring `featureFlagRoute`'s gate;
 * links declaring `hideWhenFeatureFlag` are hidden when that flag is on,
 * so a flag can swap one set of links for another.
 */
export function Navigation(props: NavigationProps) {
  const flagStates = useFeatureFlagStates();
  const isVisible = (link: NavLink) => {
    if (
      link.featureFlag !== undefined &&
      !(flagStates[link.featureFlag] ?? false)
    ) {
      return false;
    }
    if (
      link.hideWhenFeatureFlag !== undefined &&
      (flagStates[link.hideWhenFeatureFlag] ?? false)
    ) {
      return false;
    }
    return true;
  };

  return (
    <div>
      <ul className={styles.navList}>
        {navGroups.features.filter(isVisible).map((link) => (
          <li className={styles.navItem} key={link.name}>
            <NavItem {...link} onClick={props.onNavigate} />
          </li>
        ))}
      </ul>

      <RobotoDivider />

      <ul className={styles.navList}>
        {navGroups.actionsAndAgents.filter(isVisible).map((link) => (
          <li className={styles.navItem} key={link.name}>
            <NavItem {...link} onClick={props.onNavigate} />
          </li>
        ))}
      </ul>

      <RobotoDivider />

      <ul className={styles.navList}>
        {navGroups.settingsAndGuides.filter(isVisible).map((link) => (
          <li className={styles.navItem} key={link.name}>
            <NavItem {...link} onClick={props.onNavigate} />
          </li>
        ))}
      </ul>

      <SystemUserOnly>
        <RobotoDivider />
        <ul className={styles.navList}>
          {navGroups.admin.filter(isVisible).map((link) => (
            <li className={styles.navItem} key={link.name}>
              <NavItem {...link} onClick={props.onNavigate} />
            </li>
          ))}
        </ul>
      </SystemUserOnly>
    </div>
  );
}

/**
 * A drawer navigation link button
 */
function NavItem(props: NavLink & { onClick?: () => void }) {
  const match = useMatch(props.link);
  const isActive = match !== null;

  const isExternal = props.isExternalLink;

  return (
    <Link
      to={props.link}
      target={isExternal ? "_blank" : "_self"}
      rel={isExternal ? "noopener noreferrer" : undefined}
      aria-label={isExternal ? `${props.name} (opens in new tab)` : props.name}
      className={classNames(styles.navLink, {
        [styles.active]: isActive,
      })}
      onClick={props.onClick}
    >
      <props.icon className={styles.navIcon} aria-hidden="true" />
      <RobotoTypography variant="body2" className={styles.linkText}>
        {props.name}
      </RobotoTypography>
    </Link>
  );
}
